// /home/labdoo/zitech-nexus/frontend/src/components/Website/PortfolioSection.tsx
import React, { useState, useEffect } from 'react';
import { portfolioAPI } from '../../services/api';
import './PortfolioSection.css';

interface Project {
  id: string;
  title: string;
  category: string;
  category_display: string;
  description: string;
  technologies: string[];
  client: string;
  year: string;
  features: string[];
  cover_image?: string;
  gallery_images?: string[];
  live_demo_url?: string;
  github_url?: string;
  is_featured: boolean;
}

interface Solution {
  id: string;
  title: string;
  description: string;
  icon_name: string;
  benefits: string[];
  technologies: string[];
  detailed_description?: string;
  use_cases?: string[];
  is_featured: boolean;
}

const PortfolioSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'projects' | 'solutions'>('projects');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, solutionsRes] = await Promise.all([
        portfolioAPI.getProjects(),
        portfolioAPI.getSolutions()
      ]);
      setProjects(projectsRes.data);
      setSolutions(solutionsRes.data);
    } catch (err: any) {
      console.error('Error fetching portfolio data:', err);
      setError('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const getIconByName = (iconName: string) => {
    switch(iconName) {
      case 'digital_transformation':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 22V12" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M9 10.5l3-1.5 3 1.5" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      case 'cloud_migration':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      case 'cybersecurity':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L3 7l9 5 9-5-9-5z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 12v8" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M9 16l3 2 3-2" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      case 'data_analytics':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 12v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4v-3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 2v8M8 6l4-4 4 4" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      case 'devops':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4v16h16V4H4z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 8h8M8 12h6M8 16h4" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      case 'ai_ml':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M12 5v2M12 17v2M5 12H3M21 12h-2M7.05 7.05L5.64 5.64M18.36 18.36l-1.41-1.41M7.05 16.95l-1.41 1.41M18.36 5.64l-1.41 1.41" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <section id="portfolio" className="portfolio-section">
        <div className="portfolio-container">
          <div className="loading-spinner">Loading portfolio...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="portfolio" className="portfolio-section">
        <div className="portfolio-container">
          <div className="error-message">{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="portfolio-section">
      <div className="portfolio-container">
        <div className="portfolio-header">
          <h2 className="portfolio-title">Our Portfolio & Solutions</h2>
          <p className="portfolio-subtitle">
            Explore our successful projects and innovative solutions that drive business growth
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="portfolio-tabs">
          <button 
            className={`tab-btn ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            Projects ({projects.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'solutions' ? 'active' : ''}`}
            onClick={() => setActiveTab('solutions')}
          >
            Solutions ({solutions.length})
          </button>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="projects-list">
            {projects.map((project) => (
              <div key={project.id} className="project-item">
                <div className="project-header">
                  <span className="project-category">{project.category_display}</span>
                  <h3 className="project-title">{project.title}</h3>
                </div>
                <p className="project-description">{project.description}</p>
                <div className="project-tech">
                  {project.technologies.slice(0, 4).map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                  {project.technologies.length > 4 && (
                    <span className="tech-tag">+{project.technologies.length - 4}</span>
                  )}
                </div>
                <button 
                  className="view-details-btn"
                  onClick={() => setSelectedProject(project)}
                >
                  View Details →
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Solutions Tab */}
        {activeTab === 'solutions' && (
          <div className="solutions-list">
            {solutions.map((solution) => (
              <div key={solution.id} className="solution-item">
                <div className="solution-icon">{getIconByName(solution.icon_name)}</div>
                <div className="solution-content">
                  <h3 className="solution-title">{solution.title}</h3>
                  <p className="solution-description">{solution.description}</p>
                  <button 
                    className="learn-more-btn"
                    onClick={() => setSelectedSolution(solution)}
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Project Modal */}
      {selectedProject && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal-content project-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProject(null)}>✕</button>
            <div className="project-modal-header">
              <h3 className="project-modal-title">{selectedProject.title}</h3>
              <span className="project-modal-category">{selectedProject.category_display}</span>
            </div>
            <div className="project-modal-body">
              <p className="project-modal-description">{selectedProject.description}</p>
              <div className="project-details">
                <div className="detail-item">
                  <strong>Client:</strong> {selectedProject.client}
                </div>
                <div className="detail-item">
                  <strong>Year:</strong> {selectedProject.year}
                </div>
              </div>
              <div className="project-features">
                <strong>Key Features:</strong>
                <ul>
                  {selectedProject.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div className="project-technologies">
                <strong>Technologies Used:</strong>
                <div className="tech-list">
                  {selectedProject.technologies.map((tech, idx) => (
                    <span key={idx} className="tech-badge">{tech}</span>
                  ))}
                </div>
              </div>
              {selectedProject.live_demo_url && (
                <a href={selectedProject.live_demo_url} target="_blank" rel="noopener noreferrer" className="project-link">
                  View Live Demo →
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Solution Modal */}
      {selectedSolution && (
        <div className="modal-overlay" onClick={() => setSelectedSolution(null)}>
          <div className="modal-content solution-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedSolution(null)}>✕</button>
            <div className="solution-modal-header">
              <div className="solution-modal-icon">{getIconByName(selectedSolution.icon_name)}</div>
              <h3 className="solution-modal-title">{selectedSolution.title}</h3>
            </div>
            <div className="solution-modal-body">
              <p className="solution-modal-description">{selectedSolution.description}</p>
              {selectedSolution.detailed_description && (
                <p className="solution-detailed-description">{selectedSolution.detailed_description}</p>
              )}
              <div className="solution-benefits">
                <strong>Key Benefits:</strong>
                <ul>
                  {selectedSolution.benefits.map((benefit, idx) => (
                    <li key={idx}>{benefit}</li>
                  ))}
                </ul>
              </div>
              <div className="solution-technologies">
                <strong>Technologies:</strong>
                <div className="tech-list">
                  {selectedSolution.technologies.map((tech, idx) => (
                    <span key={idx} className="tech-badge">{tech}</span>
                  ))}
                </div>
              </div>
              {selectedSolution.use_cases && selectedSolution.use_cases.length > 0 && (
                <div className="solution-use-cases">
                  <strong>Use Cases:</strong>
                  <ul>
                    {selectedSolution.use_cases.map((useCase, idx) => (
                      <li key={idx}>{useCase}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;